import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import AffiliateDropdown from 'feature/dispatching/components/sidebar/AffiliateDropdown';
import ViewByDropdown from 'feature/dispatching/components/sidebar/ViewByDropdown';
import FiltersSkeleton from 'feature/dispatching/components/sidebar/FiltersSkeleton';
import MobileFilters from 'feature/dispatching/components/sidebar/MobileFilters';
import SelectorCard from 'feature/dispatching/components/sidebar/SelectorCard';
import SelectorCardCount from 'feature/dispatching/components/sidebar/SelectorCardCount';
import SelectorSkeleton from './SelectorSkeleton';
import { useViewLoadsSummaryByDateQuery } from '../loadsEndpoints';

const sidebarStyles = {
  bgcolor: 'surface.white',
  border: 1,
  borderBottom: 0,
  borderColor: 'graphic.light',
  borderLeft: 0,
  borderTop: 0,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  pb: 2,
  position: 'fixed',
  // eslint-disable-next-line
  top: { xs: 56, sm: 64, md: 64, lg: 48 },
  // eslint-disable-next-line
  width: { xs: 1, sm: 1, md: 300, lg: 300 },
};

const scrollbarStyles = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  overflowY: 'scroll',
  pb: 4,
  scrollbarWidth: 'none',
};

const ViewSummaryByDate = () => {
  const navigate = useNavigate();
  const { viewById } = useParams();
  const mdAndDown = theme => theme.breakpoints.down('md');
  const mdDown = useMediaQuery(mdAndDown, { noSsr: true });
  const [selectorsFilter, setSelectorsFilter] = useState('');

  const { data: loadsQuery = [], isLoading } = useViewLoadsSummaryByDateQuery();

  const filterFormats = createdDate => {
    const d = new Date(createdDate);
    return `
      ${createdDate}
      ${format(d, 'MMMM dd, yyyy')}
      ${format(d, 'MM/dd/yyyy')}
      ${format(d, 'M/dd/yyyy')}
      ${format(d, 'MM/d/yyyy')}
      ${format(d, 'M/d/yyyy')}
    `;
  };
  const dateFilter = R.compose(
    R.includes(R.toUpper(selectorsFilter)),
    R.toUpper,
    filterFormats,
    R.prop('createdDate')
  );
  const filteredDates = loadsQuery.filter(dateFilter);

  // prettier-ignore
  const handleSelectorsFilterChange = R.compose(
    setSelectorsFilter,
    R.path(['target', 'value'])
  );

  // prettier-ignore
  const handleSelectorClick = R.curryN(2, R.compose(
    navigate,
    R.concat('../loads/date/')
  ));

  // prettier-ignore
  const handleAffiliateChange = R.compose(
    setSelectorsFilter,
    R.always('')
  );

  useEffect(() => {
    // prettier-ignore
    const autoSelectFirst = R.and(
      R.not(mdDown), R.and(
      R.isNil(viewById),
      R.gt(loadsQuery.length, 0)
    ));

    if (autoSelectFirst) {
      const [{ createdDate }] = loadsQuery;
      navigate(`../loads/date/${createdDate}`, { replace: true });
    }
  }, [loadsQuery, mdDown, navigate, viewById]);

  const filters = (
    <Stack gap={2} sx={{ px: 2, py: 3 }}>
      <AffiliateDropdown onChange={handleAffiliateChange} />
      <ViewByDropdown module="loads">
        <MenuItem value="date">Date Created</MenuItem>
        <MenuItem value="destination">Destination</MenuItem>
        <MenuItem value="location">Location</MenuItem>
        <MenuItem value="driver">Driver</MenuItem>
      </ViewByDropdown>
      <TextField
        label="Filter Dates"
        onChange={handleSelectorsFilterChange}
        value={selectorsFilter}
      />
    </Stack>
  );

  if (isLoading)
    return (
      <Box sx={sidebarStyles}>
        {mdDown ? (
          <MobileFilters>
            <FiltersSkeleton />
          </MobileFilters>
        ) : (
          <FiltersSkeleton />
        )}
        <Divider />
        <SelectorSkeleton />
        <SelectorSkeleton />
        <SelectorSkeleton />
      </Box>
    );

  return (
    <Box sx={sidebarStyles}>
      {mdDown ? <MobileFilters>{filters}</MobileFilters> : filters}
      <Divider />
      <Box sx={scrollbarStyles}>
        {filteredDates.map(({ createdDate, draftLoadsCount, submittedLoadsCount }) => (
          <SelectorCard
            key={createdDate}
            onClick={handleSelectorClick(createdDate)}
            selected={R.equals(viewById, createdDate)}
            title={format(new Date(createdDate), 'MMMM dd, yyyy').toUpperCase()}
          >
            <SelectorCardCount isActive label="Submitted Loads" value={submittedLoadsCount} />
            <SelectorCardCount label="Draft Loads" value={draftLoadsCount} />
          </SelectorCard>
        ))}
      </Box>
    </Box>
  );
};

export default ViewSummaryByDate;
